import React, { useState, useEffect } from 'react';
import { examCalendar, examFees, examSubjectsEnhanced, studyMethods } from '../lib/supabase';
import examResources from '../data/exam-resources-jamaica.json';

export const ExamTrackerPage = ({ setPage, goBack }) => {
  const [activeTab, setActiveTab] = useState('calendar');
  const [myExams, setMyExams] = useState(() => {
    const saved = localStorage.getItem('jamaicaSchoolsGuide_myExams');
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [examType, setExamType] = useState('csec');
  const [loading, setLoading] = useState(true);
  const [calendarDates, setCalendarDates] = useState([]);
  const [fees, setFees] = useState({ csec: {}, cape: {} });
  const [csecSubjects, setCsecSubjects] = useState([]);
  const [studyMethodsList, setStudyMethodsList] = useState([]);

  // Save myExams to localStorage
  useEffect(() => {
    localStorage.setItem('jamaicaSchoolsGuide_myExams', JSON.stringify(myExams));
  }, [myExams]);

  // Load data from Supabase with JSON fallback
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Load exam calendar
        const calendarResult = await examCalendar.getAll();
        if (!calendarResult.error && calendarResult.data && calendarResult.data.length > 0) {
          setCalendarDates(calendarResult.data.map(d => ({
            date: d.event_date,
            event: d.event_name,
            examType: d.exam_type.toUpperCase()
          })));
        } else {
          // Fallback to JSON
          const allDates = [];
          if (examResources.exam_calendars?.csec?.key_dates_2026) {
            examResources.exam_calendars.csec.key_dates_2026.forEach(d => {
              allDates.push({ ...d, examType: 'CSEC' });
            });
          }
          if (examResources.exam_calendars?.cape?.key_dates_2026) {
            examResources.exam_calendars.cape.key_dates_2026.forEach(d => {
              allDates.push({ ...d, examType: 'CAPE' });
            });
          }
          if (examResources.exam_calendars?.gsat_pep?.key_dates_2026) {
            examResources.exam_calendars.gsat_pep.key_dates_2026.forEach(d => {
              allDates.push({ ...d, examType: 'PEP' });
            });
          }
          setCalendarDates(allDates);
        }

        // Load exam fees
        const feesResult = await examFees.getAll();
        if (!feesResult.error && feesResult.data && feesResult.data.length > 0) {
          const csecFees = {};
          const capeFees = {};
          feesResult.data.forEach(f => {
            if (f.exam_type === 'csec') {
              csecFees[f.fee_type] = f.amount;
            } else if (f.exam_type === 'cape') {
              capeFees[f.fee_type] = f.amount;
            }
          });
          setFees({ csec: csecFees, cape: capeFees });
        } else {
          // Fallback to JSON
          setFees({
            csec: examResources.exam_calendars?.csec?.fees || {},
            cape: examResources.exam_calendars?.cape?.fees || {}
          });
        }

        // Load subjects
        const subjectsResult = await examSubjectsEnhanced.getByExamType('csec');
        if (!subjectsResult.error && subjectsResult.data && subjectsResult.data.length > 0) {
          setCsecSubjects(subjectsResult.data.map(s => ({
            code: s.subject_code,
            name: s.name,
            type: s.subject_type,
            components: s.components || [],
            study_tips: s.study_tips || [],
            resources: s.resources || [],
            common_mistakes: s.common_mistakes || []
          })));
        } else {
          // Fallback to JSON
          setCsecSubjects(examResources.subjects?.csec || []);
        }

        // Load study methods
        const methodsResult = await studyMethods.getAll();
        if (!methodsResult.error && methodsResult.data && methodsResult.data.length > 0) {
          setStudyMethodsList(methodsResult.data.map(m => ({
            name: m.name,
            description: m.description,
            best_for: m.best_for,
            tools: m.tools || []
          })));
        } else {
          // Fallback to JSON
          setStudyMethodsList(examResources.study_methods || []);
        }
      } catch (err) {
        console.error('Error loading exam data:', err);
        // Fallback to JSON data
        const allDates = [];
        if (examResources.exam_calendars?.csec?.key_dates_2026) {
          examResources.exam_calendars.csec.key_dates_2026.forEach(d => {
            allDates.push({ ...d, examType: 'CSEC' });
          });
        }
        if (examResources.exam_calendars?.cape?.key_dates_2026) {
          examResources.exam_calendars.cape.key_dates_2026.forEach(d => {
            allDates.push({ ...d, examType: 'CAPE' });
          });
        }
        if (examResources.exam_calendars?.gsat_pep?.key_dates_2026) {
          examResources.exam_calendars.gsat_pep.key_dates_2026.forEach(d => {
            allDates.push({ ...d, examType: 'PEP' });
          });
        }
        setCalendarDates(allDates);
        setFees({
          csec: examResources.exam_calendars?.csec?.fees || {},
          cape: examResources.exam_calendars?.cape?.fees || {}
        });
        setCsecSubjects(examResources.subjects?.csec || []);
        setStudyMethodsList(examResources.study_methods || []);
      }
      setLoading(false);
    };
    loadData();
  }, []);

  const addExam = (subject) => {
    if (!myExams.find(e => e.code === subject.code && e.type === examType)) {
      setMyExams([...myExams, { ...subject, type: examType, addedAt: new Date().toISOString() }]);
    }
  };

  const removeExam = (subjectCode, type) => {
    setMyExams(myExams.filter(e => !(e.code === subjectCode && e.type === type)));
  };

  const isExamAdded = (subjectCode) => {
    return myExams.some(e => e.code === subjectCode && e.type === examType);
  };

  const getUpcomingDates = () => {
    const now = new Date();
    return calendarDates
      .filter(d => new Date(d.date) >= now)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-JM', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysUntil = (dateStr) => {
    const now = new Date();
    const target = new Date(dateStr);
    const diff = Math.ceil((target - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const tabs = [
    { id: 'calendar', name: 'Exam Calendar', icon: '📅' },
    { id: 'myexams', name: 'My Exams', icon: '📝' },
    { id: 'subjects', name: 'Subject Guides', icon: '📚' },
    { id: 'tips', name: 'Study Tips', icon: '💡' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-6 sm:py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={goBack}
            className="mb-3 sm:mb-4 text-blue-100 hover:text-white flex items-center gap-2 text-sm sm:text-base"
          >
            ← Back
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Exam Tracker & Study Guide</h1>
          <p className="text-blue-100 text-sm sm:text-base">Track your exams, access study resources, and stay organized</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4 -mt-4">
        <div className="bg-white rounded-lg shadow-md p-1 flex flex-wrap gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 min-w-[120px] py-3 px-4 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span>{tab.icon}</span>
              <span className="hidden sm:inline">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading exam information...</p>
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && !loading && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Upcoming Important Dates</h2>
              <div className="space-y-3">
                {getUpcomingDates().slice(0, 10).map((date, idx) => {
                  const daysUntil = getDaysUntil(date.date);
                  let urgencyColor = 'bg-gray-100 text-gray-700';
                  if (daysUntil <= 7) urgencyColor = 'bg-red-100 text-red-700';
                  else if (daysUntil <= 30) urgencyColor = 'bg-orange-100 text-orange-700';
                  else if (daysUntil <= 90) urgencyColor = 'bg-yellow-100 text-yellow-700';

                  return (
                    <div key={idx} className={`p-4 rounded-lg ${urgencyColor} flex flex-col sm:flex-row sm:items-center justify-between gap-2`}>
                      <div>
                        <span className="font-semibold">{date.examType}</span>
                        <span className="mx-2">•</span>
                        <span>{date.event}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span>{formatDate(date.date)}</span>
                        <span className="font-bold">
                          {daysUntil === 0 ? 'TODAY!' : daysUntil === 1 ? 'Tomorrow' : `${daysUntil} days`}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Exam Fees Info */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">CSEC Exam Fees (2026)</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Per Subject:</span>
                    <span className="font-semibold">J${(fees.csec.per_subject || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Late Registration Fee:</span>
                    <span className="font-semibold">J${(fees.csec.late_fee || 0).toLocaleString()}</span>
                  </div>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm">
                    <strong>Example:</strong> 8 subjects = J${((fees.csec.per_subject || 0) * 8).toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">CAPE Exam Fees (2026)</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Per Unit:</span>
                    <span className="font-semibold">J${(fees.cape.per_unit || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Late Registration Fee:</span>
                    <span className="font-semibold">J${(fees.cape.late_fee || 0).toLocaleString()}</span>
                  </div>
                  <div className="mt-4 p-3 bg-purple-50 rounded-lg text-sm">
                    <strong>Note:</strong> Each CAPE subject has 2 units
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* My Exams Tab */}
        {activeTab === 'myexams' && !loading && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-md p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h2 className="text-xl font-bold text-gray-800">My Exam Subjects</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => setExamType('csec')}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      examType === 'csec' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    CSEC
                  </button>
                  <button
                    onClick={() => setExamType('cape')}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      examType === 'cape' ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    CAPE
                  </button>
                </div>
              </div>

              {myExams.filter(e => e.type === examType).length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <div className="text-4xl mb-4">📝</div>
                  <p>You haven't added any {examType.toUpperCase()} subjects yet.</p>
                  <p className="text-sm mt-2">Go to "Subject Guides" to add subjects you're taking.</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {myExams.filter(e => e.type === examType).map(exam => (
                    <div key={`${exam.code}-${exam.type}`} className="border rounded-lg p-4 flex justify-between items-center">
                      <div>
                        <h3 className="font-semibold text-gray-800">{exam.name}</h3>
                        <p className="text-sm text-gray-600">{exam.type.toUpperCase()} • {exam.components?.length || 0} components</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setSelectedSubject(exam);
                            setActiveTab('subjects');
                          }}
                          className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200"
                        >
                          Study Guide
                        </button>
                        <button
                          onClick={() => removeExam(exam.code, exam.type)}
                          className="px-3 py-1 bg-red-100 text-red-700 rounded-lg text-sm hover:bg-red-200"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {myExams.filter(e => e.type === examType).length > 0 && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Estimated Fees</h4>
                  <p className="text-2xl font-bold text-blue-600">
                    J${(myExams.filter(e => e.type === examType).length *
                      (examType === 'csec' ? (fees.csec.per_subject || 0) : (fees.cape.per_unit || 0))
                    ).toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    For {myExams.filter(e => e.type === examType).length} {examType === 'csec' ? 'subjects' : 'units'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Subject Guides Tab */}
        {activeTab === 'subjects' && !loading && (
          <div className="space-y-6">
            {selectedSubject ? (
              <div className="bg-white rounded-xl shadow-md p-6">
                <button
                  onClick={() => setSelectedSubject(null)}
                  className="mb-4 text-blue-600 hover:text-blue-800 flex items-center gap-2"
                >
                  ← Back to all subjects
                </button>

                <div className="flex justify-between items-start mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">{selectedSubject.name}</h2>
                  {!isExamAdded(selectedSubject.code) && (
                    <button
                      onClick={() => addExam(selectedSubject)}
                      className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
                    >
                      + Add to My Exams
                    </button>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Components */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-800 mb-3">Exam Components</h3>
                    <ul className="space-y-2">
                      {selectedSubject.components?.map((comp, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
                          {comp}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Study Tips */}
                  <div className="bg-brand-50 rounded-lg p-4">
                    <h3 className="font-semibold text-brand-800 mb-3">Study Tips</h3>
                    <ul className="space-y-2">
                      {selectedSubject.study_tips?.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <span className="text-brand-600 mt-1">✓</span>
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Resources */}
                {selectedSubject.resources && selectedSubject.resources.length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-semibold text-gray-800 mb-3">Study Resources</h3>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {selectedSubject.resources.map((resource, idx) => (
                        <div key={idx} className="border rounded-lg p-4">
                          <div className="text-sm text-gray-500 mb-1">{resource.type}</div>
                          <div className="font-medium">{resource.name}</div>
                          {resource.available_at && (
                            <div className="text-sm text-gray-600 mt-1">Available at: {resource.available_at}</div>
                          )}
                          {resource.url && (
                            <a
                              href={`https://${resource.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 text-sm hover:underline mt-1 block"
                            >
                              Visit →
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Common Mistakes */}
                {selectedSubject.common_mistakes && selectedSubject.common_mistakes.length > 0 && (
                  <div className="mt-6 bg-red-50 rounded-lg p-4">
                    <h3 className="font-semibold text-red-800 mb-3">Common Mistakes to Avoid</h3>
                    <ul className="space-y-2">
                      {selectedSubject.common_mistakes.map((mistake, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <span className="text-red-600">⚠</span>
                          {mistake}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-800">CSEC Subject Guides</h2>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {csecSubjects.map(subject => (
                    <div
                      key={subject.code}
                      className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => setSelectedSubject(subject)}
                    >
                      <div className="flex justify-between items-start">
                        <h3 className="font-semibold text-gray-800">{subject.name}</h3>
                        {subject.type === 'compulsory' && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Required</span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        {subject.components?.length || 0} components
                      </p>
                      <div className="mt-3 flex justify-between items-center">
                        <span className="text-blue-600 text-sm">View guide →</span>
                        {isExamAdded(subject.code) ? (
                          <span className="text-brand-600 text-sm">✓ Added</span>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              addExam(subject);
                            }}
                            className="text-sm bg-brand-100 text-brand-700 px-2 py-1 rounded hover:bg-brand-200"
                          >
                            + Add
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Study Tips Tab */}
        {activeTab === 'tips' && !loading && (
          <div className="space-y-6">
            {/* Study Methods */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Proven Study Methods</h2>
              <div className="grid md:grid-cols-2 gap-4">
                {studyMethodsList.map((method, idx) => (
                  <div key={idx} className="border rounded-lg p-4">
                    <h3 className="font-semibold text-gray-800 mb-2">{method.name}</h3>
                    <p className="text-gray-600 text-sm mb-3">{method.description}</p>
                    <div className="text-sm">
                      <span className="font-medium">Best for:</span> {method.best_for}
                    </div>
                    {method.tools && method.tools.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {method.tools.map((tool, tidx) => (
                          <span key={tidx} className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {tool}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Before Exam */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Before the Exam</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {examResources.exam_tips.before_exam.map((tip, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
                    <span className="text-blue-600">✓</span>
                    <span className="text-sm">{tip}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* During Exam */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">During the Exam</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {examResources.exam_tips.during_exam.map((tip, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-3 bg-brand-50 rounded-lg">
                    <span className="text-brand-600">→</span>
                    <span className="text-sm">{tip}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Common Mistakes */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Common Mistakes to Avoid</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {examResources.exam_tips.common_mistakes.map((mistake, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-3 bg-red-50 rounded-lg">
                    <span className="text-red-600">⚠</span>
                    <span className="text-sm">{mistake}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
